import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/useDebounce";
import {
  Search,
  UserPlus,
  UserMinus,
  Users,
  Stethoscope,
  History,
  Loader2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import BillingSidebar from "@/components/layout/BillingSidebar";
import BillingHeader from "@/components/layout/BillingHeader";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";

// Types
interface Profile {
  id: string;
  display_name: string | null;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  email: string | null;
}

function formatFullName(profile: Profile): string {
  const parts = [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : profile.display_name || "Unknown";
}

interface Assignment {
  id: string;
  provider_id: string;
  member_id: string;
  created_at: string;
}

interface AssignmentLog {
  id: string;
  provider_id: string;
  member_id: string;
  action: string;
  performed_by: string | null;
  performed_by_role: string | null;
  reason: string | null;
  created_at: string;
}

// ─── Search by Member Tab ─────────────────────────────────────────────
function SearchByMemberTab() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const [expandedMember, setExpandedMember] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [providerSearch, setProviderSearch] = useState("");
  const debouncedProviderSearch = useDebounce(providerSearch);
  const queryClient = useQueryClient();

  // Search members (only those with 'member' role)
  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ["billing-members-search", debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data: memberRoles } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "member");
      const memberIds = (memberRoles || []).map((r: any) => r.user_id);
      if (memberIds.length === 0) return [];

      const { data } = await supabase
        .from("profiles")
        .select("id, display_name, first_name, middle_name, last_name, email")
        .in("id", memberIds)
        .or(`display_name.ilike.%${debouncedSearch}%,first_name.ilike.%${debouncedSearch}%,last_name.ilike.%${debouncedSearch}%`)
        .limit(20);
      return (data || []) as Profile[];
    },
    enabled: debouncedSearch.length >= 2,
  });

  // Get assignments for expanded member
  const { data: memberAssignments = [] } = useQuery({
    queryKey: ["member-assignments", expandedMember],
    queryFn: async () => {
      if (!expandedMember) return [];
      const { data } = await supabase
        .from("provider_members")
        .select("*")
        .eq("member_id", expandedMember);
      return (data || []) as Assignment[];
    },
    enabled: !!expandedMember,
  });

  // Get provider profiles for assignments
  const providerIds = memberAssignments.map((a) => a.provider_id);
  const { data: assignedProviders = [] } = useQuery({
    queryKey: ["assigned-provider-profiles", providerIds],
    queryFn: async () => {
      if (providerIds.length === 0) return [];
      const { data } = await supabase
        .from("profiles")
        .select("id, display_name, first_name, middle_name, last_name, email")
        .in("id", providerIds);
      return (data || []) as Profile[];
    },
    enabled: providerIds.length > 0,
  });

  // Search providers for add dialog
  const { data: searchProviders = [] } = useQuery({
    queryKey: ["billing-providers-search", debouncedProviderSearch],
    queryFn: async () => {
      if (debouncedProviderSearch.length < 2) return [];
      const { data: providerRoles } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "provider");
      const providerUserIds = (providerRoles || []).map((r: any) => r.user_id);
      if (providerUserIds.length === 0) return [];

      const { data } = await supabase
        .from("profiles")
        .select("id, display_name, first_name, middle_name, last_name, email")
        .in("id", providerUserIds)
        .or(`display_name.ilike.%${debouncedProviderSearch}%,first_name.ilike.%${debouncedProviderSearch}%,last_name.ilike.%${debouncedProviderSearch}%`)
        .limit(20);
      return (data || []) as Profile[];
    },
    enabled: debouncedProviderSearch.length >= 2,
  });

  const assignMutation = useMutation({
    mutationFn: async (providerId: string) => {
      const { error } = await supabase
        .from("provider_members")
        .insert({ provider_id: providerId, member_id: selectedMemberId! } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Provider assigned successfully");
      queryClient.invalidateQueries({ queryKey: ["member-assignments"] });
      setAddDialogOpen(false);
      setProviderSearch("");
    },
    onError: (error: any) => {
      toast.error(error.message?.includes("duplicate") ? "Assignment already exists" : "Failed to assign provider");
    },
  });

  const unassignMutation = useMutation({
    mutationFn: async ({ providerId, memberId }: { providerId: string; memberId: string }) => {
      const { error } = await supabase
        .from("provider_members")
        .delete()
        .eq("provider_id", providerId)
        .eq("member_id", memberId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Provider unassigned successfully");
      queryClient.invalidateQueries({ queryKey: ["member-assignments"] });
    },
    onError: () => toast.error("Failed to unassign provider"),
  });

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search members by name (min 2 characters)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {membersLoading && <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" />Searching...</div>}

      {members.map((member) => (
        <Collapsible
          key={member.id}
          open={expandedMember === member.id}
          onOpenChange={(open) => setExpandedMember(open ? member.id : null)}
        >
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {expandedMember === member.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                        {formatFullName(member)[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm">{formatFullName(member)}</p>
                      <p className="text-xs text-muted-foreground">{member.email}</p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-xs">Member</Badge>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 pb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-muted-foreground">Assigned Providers</h4>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setSelectedMemberId(member.id); setAddDialogOpen(true); }}
                  >
                    <UserPlus className="h-4 w-4 mr-1" /> Add Provider
                  </Button>
                </div>
                {assignedProviders.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No providers assigned</p>
                ) : (
                  <div className="space-y-2">
                    {assignedProviders.map((provider) => (
                      <div key={provider.id} className="flex items-center justify-between p-2 rounded-md bg-muted/30">
                        <div className="flex items-center gap-2">
                          <Stethoscope className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{formatFullName(provider)}</span>
                          <span className="text-xs text-muted-foreground">{provider.email}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => unassignMutation.mutate({ providerId: provider.id, memberId: member.id })}
                          disabled={unassignMutation.isPending}
                        >
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      ))}

      {/* Add Provider Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Provider</DialogTitle>
            <DialogDescription>Search for a provider to assign to this member.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search providers..."
              value={providerSearch}
              onChange={(e) => setProviderSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="max-h-60 overflow-y-auto space-y-1">
            {searchProviders.map((provider) => (
              <button
                key={provider.id}
                onClick={() => assignMutation.mutate(provider.id)}
                disabled={assignMutation.isPending || providerIds.includes(provider.id)}
                className="w-full flex items-center justify-between p-2 rounded-md hover:bg-muted transition-colors text-left disabled:opacity-50"
              >
                <div className="flex items-center gap-2">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="text-xs bg-success/10 text-success">
                      {formatFullName(provider)[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium">{formatFullName(provider)}</p>
                    <p className="text-xs text-muted-foreground">{provider.email}</p>
                  </div>
                </div>
                {providerIds.includes(provider.id) && (
                  <Badge variant="outline" className="text-xs">Already assigned</Badge>
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Search by Provider Tab ───────────────────────────────────────────
function SearchByProviderTab() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [memberSearch, setMemberSearch] = useState("");
  const debouncedMemberSearch = useDebounce(memberSearch);
  const queryClient = useQueryClient();

  const { data: providers = [], isLoading: providersLoading } = useQuery({
    queryKey: ["billing-providers-search-tab", debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data: providerRoles } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "provider");
      const providerUserIds = (providerRoles || []).map((r: any) => r.user_id);
      if (providerUserIds.length === 0) return [];

      const { data } = await supabase
        .from("profiles")
        .select("id, display_name, first_name, middle_name, last_name, email")
        .in("id", providerUserIds)
        .or(`display_name.ilike.%${debouncedSearch}%,first_name.ilike.%${debouncedSearch}%,last_name.ilike.%${debouncedSearch}%`)
        .limit(20);
      return (data || []) as Profile[];
    },
    enabled: debouncedSearch.length >= 2,
  });

  const { data: providerAssignments = [] } = useQuery({
    queryKey: ["provider-assignments", expandedProvider],
    queryFn: async () => {
      if (!expandedProvider) return [];
      const { data } = await supabase
        .from("provider_members")
        .select("*")
        .eq("provider_id", expandedProvider);
      return (data || []) as Assignment[];
    },
    enabled: !!expandedProvider,
  });

  const memberIds = providerAssignments.map((a) => a.member_id);
  const { data: assignedMembers = [] } = useQuery({
    queryKey: ["assigned-member-profiles", memberIds],
    queryFn: async () => {
      if (memberIds.length === 0) return [];
      const { data } = await supabase
        .from("profiles")
        .select("id, display_name, first_name, middle_name, last_name, email")
        .in("id", memberIds);
      return (data || []) as Profile[];
    },
    enabled: memberIds.length > 0,
  });

  const { data: searchMembers = [] } = useQuery({
    queryKey: ["billing-members-search-dialog", debouncedMemberSearch],
    queryFn: async () => {
      if (debouncedMemberSearch.length < 2) return [];
      const { data: memberRoles } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "member");
      const mIds = (memberRoles || []).map((r: any) => r.user_id);
      if (mIds.length === 0) return [];

      const { data } = await supabase
        .from("profiles")
        .select("id, display_name, first_name, middle_name, last_name, email")
        .in("id", mIds)
        .or(`display_name.ilike.%${debouncedMemberSearch}%,first_name.ilike.%${debouncedMemberSearch}%,last_name.ilike.%${debouncedMemberSearch}%`)
        .limit(20);
      return (data || []) as Profile[];
    },
    enabled: debouncedMemberSearch.length >= 2,
  });

  const assignMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase
        .from("provider_members")
        .insert({ provider_id: selectedProviderId!, member_id: memberId } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Member assigned successfully");
      queryClient.invalidateQueries({ queryKey: ["provider-assignments"] });
      setAddDialogOpen(false);
      setMemberSearch("");
    },
    onError: (error: any) => {
      toast.error(error.message?.includes("duplicate") ? "Assignment already exists" : "Failed to assign member");
    },
  });

  const unassignMutation = useMutation({
    mutationFn: async ({ providerId, memberId }: { providerId: string; memberId: string }) => {
      const { error } = await supabase
        .from("provider_members")
        .delete()
        .eq("provider_id", providerId)
        .eq("member_id", memberId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Member unassigned successfully");
      queryClient.invalidateQueries({ queryKey: ["provider-assignments"] });
    },
    onError: () => toast.error("Failed to unassign member"),
  });

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search providers by name (min 2 characters)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {providersLoading && <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" />Searching...</div>}

      {providers.map((provider) => (
        <Collapsible
          key={provider.id}
          open={expandedProvider === provider.id}
          onOpenChange={(open) => setExpandedProvider(open ? provider.id : null)}
        >
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {expandedProvider === provider.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-success/10 text-success text-xs">
                        {formatFullName(provider)[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm">{formatFullName(provider)}</p>
                      <p className="text-xs text-muted-foreground">{provider.email}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs border-success/30 text-success">Provider</Badge>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 pb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-muted-foreground">Assigned Members</h4>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setSelectedProviderId(provider.id); setAddDialogOpen(true); }}
                  >
                    <UserPlus className="h-4 w-4 mr-1" /> Add Member
                  </Button>
                </div>
                {assignedMembers.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No members assigned</p>
                ) : (
                  <div className="space-y-2">
                    {assignedMembers.map((member) => (
                      <div key={member.id} className="flex items-center justify-between p-2 rounded-md bg-muted/30">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{formatFullName(member)}</span>
                          <span className="text-xs text-muted-foreground">{member.email}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => unassignMutation.mutate({ providerId: provider.id, memberId: member.id })}
                          disabled={unassignMutation.isPending}
                        >
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      ))}

      {/* Add Member Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Member</DialogTitle>
            <DialogDescription>Search for a member to assign to this provider.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search members..."
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="max-h-60 overflow-y-auto space-y-1">
            {searchMembers.map((member) => (
              <button
                key={member.id}
                onClick={() => assignMutation.mutate(member.id)}
                disabled={assignMutation.isPending || memberIds.includes(member.id)}
                className="w-full flex items-center justify-between p-2 rounded-md hover:bg-muted transition-colors text-left disabled:opacity-50"
              >
                <div className="flex items-center gap-2">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="text-xs bg-primary/10 text-primary">
                      {formatFullName(member)[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium">{formatFullName(member)}</p>
                    <p className="text-xs text-muted-foreground">{member.email}</p>
                  </div>
                </div>
                {memberIds.includes(member.id) && (
                  <Badge variant="outline" className="text-xs">Already assigned</Badge>
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Assignment Log Tab ───────────────────────────────────────────────
function AssignmentLogTab() {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["assignment-logs"],
    queryFn: async () => {
      const { data } = await supabase
        .from("provider_members_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);
      return (data || []) as AssignmentLog[];
    },
  });

  // Gather unique IDs for profile lookup
  const allIds = [...new Set(logs.flatMap((l) => [l.provider_id, l.member_id, l.performed_by].filter(Boolean) as string[]))];
  const { data: profiles = [] } = useQuery({
    queryKey: ["log-profiles", allIds],
    queryFn: async () => {
      if (allIds.length === 0) return [];
      const { data } = await supabase.from("profiles").select("id, display_name, first_name, middle_name, last_name").in("id", allIds);
      return (data || []) as Pick<Profile, "id" | "display_name" | "first_name" | "middle_name" | "last_name">[];
    },
    enabled: allIds.length > 0,
  });

  const profileMap = Object.fromEntries(profiles.map((p) => [p.id, formatFullName(p as Profile)]));

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Action</TableHead>
            <TableHead>Provider</TableHead>
            <TableHead>Member</TableHead>
            <TableHead>Performed By</TableHead>
            <TableHead>Role</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8">
                <Loader2 className="h-5 w-5 animate-spin mx-auto" />
              </TableCell>
            </TableRow>
          ) : logs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                No assignment logs yet
              </TableCell>
            </TableRow>
          ) : (
            logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell className="text-sm">{format(new Date(log.created_at), "MMM d, yyyy h:mm a")}</TableCell>
                <TableCell>
                  <Badge variant={log.action === "assigned" ? "default" : "destructive"} className="text-xs">
                    {log.action}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">{profileMap[log.provider_id] || log.provider_id.slice(0, 8)}</TableCell>
                <TableCell className="text-sm">{profileMap[log.member_id] || log.member_id.slice(0, 8)}</TableCell>
                <TableCell className="text-sm">
                  {log.performed_by ? (profileMap[log.performed_by] || log.performed_by.slice(0, 8)) : "System"}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs capitalize">{log.performed_by_role || "system"}</Badge>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────
export default function ProviderMemberAssignments() {
  return (
    <div className="flex min-h-screen bg-background">
      <BillingSidebar />
      <div className="flex-1 flex flex-col">
        <BillingHeader />
        <main className="flex-1 p-6 space-y-6">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Provider-Member Assignments</h1>
            <p className="text-muted-foreground mt-1">Manage provider-member relationships and view assignment history.</p>
          </div>

          <Tabs defaultValue="by-member" className="space-y-4">
            <TabsList>
              <TabsTrigger value="by-member" className="gap-1.5">
                <Users className="h-4 w-4" /> Search by Member
              </TabsTrigger>
              <TabsTrigger value="by-provider" className="gap-1.5">
                <Stethoscope className="h-4 w-4" /> Search by Provider
              </TabsTrigger>
              <TabsTrigger value="log" className="gap-1.5">
                <History className="h-4 w-4" /> Assignment Log
              </TabsTrigger>
            </TabsList>

            <TabsContent value="by-member">
              <SearchByMemberTab />
            </TabsContent>

            <TabsContent value="by-provider">
              <SearchByProviderTab />
            </TabsContent>

            <TabsContent value="log">
              <AssignmentLogTab />
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}
